import getCategoryByCode from "./get-category-by-code";

// A filter value counts as "inactive" when it is empty string, null or undefined.
// Note: "0" / 0 are considered active (e.g. 0 rooms/bathrooms).
const isEmpty = (value) =>
	value === undefined || value === null || value === "";

// Robust numeric coercion: accepts numbers or numeric strings (with thousand
// separators / currency symbols), returns undefined when not parseable.
const toNumber = (value) => {
	if (value === undefined || value === null || value === "") return undefined;
	if (typeof value === "number") return Number.isNaN(value) ? undefined : value;
	const cleaned = String(value).replace(/[^\d.-]/g, "");
	if (cleaned === "" || cleaned === "-" || cleaned === ".") return undefined;
	const num = Number(cleaned);
	return Number.isNaN(num) ? undefined : num;
};

// Returns the numeric `value` of a characteristic by its `name`, or undefined.
export const getCharacteristic = (characteristics, name) => {
	if (!Array.isArray(characteristics)) return undefined;
	const found = characteristics.find((item) => item && item.name === name);
	if (!found) return undefined;
	return toNumber(found.value);
};

// Pure client-side filter for the full property list.
// `filters` keys: propertyType, operation, commune, priceMin, priceMax,
// totalAreaFrom, totalAreaTo, rooms, bathrooms, currency.
export const filterProperties = (properties, filters = {}) => {
	if (!Array.isArray(properties)) return [];

	return properties.filter((property) => {
		// propertyType: compare the title-derived category label, case-insensitive
		// trimmed equality (guards against label formatting differences).
		if (!isEmpty(filters.propertyType)) {
			const category = getCategoryByCode(property.title);
			if (
				category.trim().toLowerCase() !==
				String(filters.propertyType).trim().toLowerCase()
			) {
				return false;
			}
		}

		// operation: exact match on p.operation.
		if (!isEmpty(filters.operation)) {
			if ((property.operation || "") !== filters.operation) return false;
		}

		// commune: case-insensitive trimmed equality.
		if (!isEmpty(filters.commune)) {
			const commune = (property.ubication && property.ubication.commune) || "";
			if (
				commune.trim().toLowerCase() !==
				String(filters.commune).trim().toLowerCase()
			) {
				return false;
			}
		}

		// price: compare valueUf when currency filter is UF, otherwise value.
		const useUf = String(filters.currency).toUpperCase() === "UF";
		const priceValue = toNumber(useUf ? property.valueUf : property.value);
		if (!isEmpty(filters.priceMin)) {
			const min = toNumber(filters.priceMin);
			if (min !== undefined && (priceValue === undefined || priceValue < min)) {
				return false;
			}
		}
		if (!isEmpty(filters.priceMax)) {
			const max = toNumber(filters.priceMax);
			if (max !== undefined && (priceValue === undefined || priceValue > max)) {
				return false;
			}
		}

		// surface: "Superficie total" falling back to "Superficie útil".
		let surface = getCharacteristic(
			property.characteristics,
			"Superficie total",
		);
		if (surface === undefined) {
			surface = getCharacteristic(property.characteristics, "Superficie útil");
		}
		if (!isEmpty(filters.totalAreaFrom)) {
			const from = toNumber(filters.totalAreaFrom);
			if (from !== undefined && (surface === undefined || surface < from)) {
				return false;
			}
		}
		if (!isEmpty(filters.totalAreaTo)) {
			const to = toNumber(filters.totalAreaTo);
			if (to !== undefined && (surface === undefined || surface > to)) {
				return false;
			}
		}

		// rooms: exact number of "Dormitorios".
		if (!isEmpty(filters.rooms)) {
			const rooms = getCharacteristic(property.characteristics, "Dormitorios");
			if (rooms !== Number(filters.rooms)) return false;
		}

		// bathrooms: exact number of "Baños".
		if (!isEmpty(filters.bathrooms)) {
			const baths = getCharacteristic(property.characteristics, "Baños");
			if (baths !== Number(filters.bathrooms)) return false;
		}

		return true;
	});
};

export default filterProperties;
